import About from 'FeatureModules/ProductPage/Components/MultiStep/About';
import Feedback from 'FeatureModules/ProductPage/Components/MultiStep/Feedback';
import General from 'FeatureModules/ProductPage/Components/MultiStep/General';
import ImpactFacts from 'FeatureModules/ProductPage/Components/MultiStep/ImpactFacts';
import MoreInfo from 'FeatureModules/ProductPage/Components/MultiStep/MoreInfo';
import MoreProducts from 'FeatureModules/ProductPage/Components/MultiStep/MoreProducts';
import ProductJourney from 'FeatureModules/ProductPage/Components/MultiStep/ProductJourney';
import VerifiedPayments from 'FeatureModules/ProductPage/Components/MultiStep/VerifiedPayments';
import generalSchema from 'FeatureModules/ProductPage/schemas/general.schemas';
import { contentBlockSchema } from 'FeatureModules/ProductPage/schemas/product.schemas';
import {
  EContentBlockType,
  TContentBlockType,
} from 'FeatureModules/ProductPage/types/product.types';
import { SupplyChainSchema } from 'FeatureModules/SupplyChain/schemas/supplyChain.schemas';
import { DocumentNode } from 'graphql';
import {
  UPDATE_ABOUT,
  UPDATE_FEEDBACK_BLOCK,
  UPDATE_IMPACT_FACTS,
  UPDATE_MORE_INFO_BLOCK,
  UPDATE_PRODUCTS_BLOCK,
  UPDATE_VERIFIED_PAYMENT_BLOCK,
  UPDATE_VERSION_DEFAULTS,
  UPDATE_PRODUCT_JOURNEY,
} from 'graphql/secure/mutations';
import React from 'react';
import { ObjectSchema } from 'yup';

export interface IContentBlock {
  id: TContentBlockType;
  title: string;
  description: string;
  renderContentBlock: (contentBlock: IContentBlock, previewIsActive: boolean) => JSX.Element | null;
  mutation: DocumentNode;
  schema: ObjectSchema;
  formWide: boolean;
}

export const getContentBlocks = (): IContentBlock[] => {
  const menu = FULL_VERSION_CONTENT_BLOCK_MENU;
  return [GENERAL_BLOCK, ...menu];
};

export const renderContentBlock = (contentBlock: IContentBlock, previewIsActive: boolean) => {
  switch (contentBlock.id) {
    case EContentBlockType.ABOUT:
      return <About menu={contentBlock} previewIsActive={previewIsActive} />;
    case EContentBlockType.FEEDBACK:
      return <Feedback menu={contentBlock} />;
    case EContentBlockType.GENERAL:
      return <General />;
    case EContentBlockType.IMPACT_FACTS:
      return <ImpactFacts menu={contentBlock} previewIsActive={previewIsActive} />;
    case EContentBlockType.MORE_INFO:
      return <MoreInfo menu={contentBlock} previewIsActive={previewIsActive} />;
    case EContentBlockType.PRODUCT_JOURNEY:
      return <ProductJourney menu={contentBlock} />;
    case EContentBlockType.MORE_PRODUCTS:
      return <MoreProducts menu={contentBlock} />;
    case EContentBlockType.VERIFIED_PAYMENTS:
      return <VerifiedPayments menu={contentBlock} />;
    default:
      return null;
  }
};

const GENERAL_BLOCK: IContentBlock = {
  id: EContentBlockType.GENERAL,
  title: 'General',
  description: 'General info about your company and product.',
  renderContentBlock,
  mutation: UPDATE_VERSION_DEFAULTS,
  schema: generalSchema,
  formWide: true,
};

const FULL_VERSION_CONTENT_BLOCK_MENU: IContentBlock[] = [
  {
    id: EContentBlockType.ABOUT,
    title: 'About',
    description: `
      The about section is meant to give the user a quick overview about your
      company and presented product. It displays your logo along with other company
      information as well as free text and image.
    `,
    renderContentBlock,
    mutation: UPDATE_ABOUT,
    schema: contentBlockSchema.ABOUT_BLOCK.schema,
    formWide: true,
  },
  {
    id: EContentBlockType.PRODUCT_JOURNEY,
    title: 'Product Journey',
    description: `
      The Product Journey is the heart of your product story. It is an animated
      map with each step of your supply chain as well as information about that step.
    `,
    renderContentBlock,
    mutation: UPDATE_PRODUCT_JOURNEY,
    schema: SupplyChainSchema,
    formWide: true,
  },
  {
    id: EContentBlockType.IMPACT_FACTS,
    title: 'Impact',
    description: `
      The Impact element is a collection of all the good you're doing with your
      product. Provide information about the benefits your product has along it's
      supply chain and align it with the Sustainable Development Goals.
    `,
    renderContentBlock,
    mutation: UPDATE_IMPACT_FACTS,
    schema: contentBlockSchema.IMPACT_FACTS_BLOCK.schema,
    formWide: true,
  },
  {
    id: EContentBlockType.MORE_PRODUCTS,
    title: 'More Products',
    description: `
      Here you can display more of your products and provide links to your online
      shop.
    `,
    renderContentBlock,
    mutation: UPDATE_PRODUCTS_BLOCK,
    schema: contentBlockSchema.PRODUCTS_BLOCK.schema,
    formWide: true,
  },

  {
    id: EContentBlockType.MORE_INFO,
    title: 'Additional Info',
    description: `
      This is a variable block that can contain more information of your
      own choosing. You could add more details about your product, ingredients
      or something entirely else.
    `,
    renderContentBlock,
    mutation: UPDATE_MORE_INFO_BLOCK,
    schema: contentBlockSchema.MORE_INFO_BLOCK.schema,
    formWide: true,
  },
  {
    id: EContentBlockType.FEEDBACK,
    title: 'Feedback',
    renderContentBlock,
    description: `
      The feedback function let's the user rate your product on a scale of 1 - 5 and also leave you a message. This way you can collect valuable data to improve your products.
    `,
    mutation: UPDATE_FEEDBACK_BLOCK,
    schema: contentBlockSchema.FEEDBACK_BLOCK.schema,
    formWide: true,
  },
  {
    id: EContentBlockType.VERIFIED_PAYMENTS,
    title: 'Verified Payments',
    description: `
      This block lets you enter meta data about your fair payments project.
    `,
    renderContentBlock,
    mutation: UPDATE_VERIFIED_PAYMENT_BLOCK,
    schema: contentBlockSchema.VERIFIED_PAYMENT_BLOCK.schema,
    formWide: true,
  },
];
